import { useTranslation } from "react-i18next";
import { Badge } from "@/shared/components/ui/badge";

interface TestPartitionBadgeProps {
  /**
   * `User.isTestAccount`, or `Property.isTest` / `Venue.isTest`.
   *
   * Accepts `undefined` on purpose: the field is absent from admin
   * responses on any API build that predates ADR-0009, and a hard
   * requirement here would turn a contract gap into a render crash.
   */
  readonly isTest: boolean | undefined;
  /** Override so list rows can scope an assertion to one entity. */
  readonly testId?: string;
}

/**
 * The "TEST" pill for entities in the ADR-0009 prod test partition
 * (store-review demo accounts + QA smoke fixtures).
 *
 * Lives in `shared/` because three feature slices render it — users,
 * venues, properties — and features may never import each other.
 *
 * Staff see BOTH partitions, always: this badge is the *only* thing
 * that tells an operator a venue is a demo fixture, so it is never
 * hidden and there is no partition filter on the listings.
 */
export function TestPartitionBadge({
  isTest,
  testId = "test-partition-badge",
}: TestPartitionBadgeProps) {
  const { t } = useTranslation();

  // Strict `!== true`, not a falsy check: an absent flag must read as
  // "real", never as "test". Mislabelling a real venue as a demo is the
  // dangerous direction — an operator could dismiss a live payout issue
  // as test noise.
  if (isTest !== true) return null;

  const tooltip = t("testPartition.tooltip");

  return (
    <Badge data-testid={testId} variant="test" title={tooltip} aria-label={tooltip}>
      {t("testPartition.badge")}
    </Badge>
  );
}
